// A small one-bit picture of a lit knock's neighbour (sprint 21): the overview's card (the whole figure) and the envelope's
// row (head and shoulders). Drawn from the same masks as the knock itself (litMask in neighbours-lit.js), knocking pose: a
// pane of dark glass, a straight beam down from the lamp at the top, the figure in ink in it with a one-pixel paper rim where
// it meets the dark (lit from behind), on the sill. Ink and paper only; no knock light (that stays on the knock's glass,
// plate and tag). Lintel is hollow, so the beam shows through the wire; its two dot eyes are ink in the lit head, Pinch's
// are paper on the dark rider.
import { litMask, isLit, isBearLook } from './neighbours-lit.js';
import { holdenMask } from './neighbours-holden.js';

// Bayer 4x4, for the beam's soft edge (one column either side, 50%)
const B4 = [[0, 8, 2, 10], [12, 4, 14, 6], [3, 11, 1, 9], [15, 7, 13, 5]];
// both looks at one size, so the two pictures compare: mask rows from just above the tallest top (Lintel's head)
const TOP = 66, SUB = 4, HEAD = 2;
// the head-and-shoulders crop: this many mask rows from TOP (Pinch's rider and collar, Lintel's head and arch)
const BUST = 150;

// The picture as w*h bits, row by row: 1 ink, 0 paper. bust: head and shoulders, no sill. null for anything but a lit look.
export function litIconBits(look, w = 60, h = 80, { bust = false } = {}) {
  if (!isLit(look) && !isBearLook(look)) return null;
  const M = isBearLook(look) ? holdenMask(0) : litMask(look, 0), out = new Uint8Array(w * h); // Mr Holden's figure is the sheet drawn in Aseprite (sprint 36)
  if (!M) return null;
  const sill = bust ? h : h - 3;
  const k = (bust ? BUST : M.feet - TOP) / (sill - HEAD); // mask px per picture px
  const fig = new Uint8Array(w * h);
  // the figure: a picture pixel is ink when at least 3 in 16 samples of its block are on the mask (the thin legs and wire stay)
  for (let y = 0; y < sill; y++) for (let x = 0; x < w; x++) {
    let n = 0;
    for (let j = 0; j < SUB; j++) for (let i = 0; i < SUB; i++) {
      const mx = Math.floor(M.cx + (x - w / 2 + (i + 0.5) / SUB) * k), my = Math.floor(TOP + (y - HEAD + (j + 0.5) / SUB) * k);
      if (mx >= 0 && my >= 0 && mx < M.w && my < M.h && M.m[my * M.w + mx]) n++;
    }
    fig[y * w + x] = n >= 3 ? 1 : 0;
  }
  // the beam: from the lamp's mouth at the top centre, a cone to most of the sill, wide enough at the rider's height to hold
  // Pinch's spread knees (as the knock's beam holds the horn tips)
  // (the crop is closer, so its beam is wider)
  const half = bust ? y => w * (0.42 + 0.06 * (y / (h - 1))) : y => w * (0.27 + 0.19 * (y / (h - 1)));
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const d = Math.abs(x + 0.5 - w / 2) - half(y);
    out[y * w + x] = y > sill || d < -1 || (d < 1 && B4[y & 3][x & 3] < 8) ? 0 : 1;
  }
  // the sill: an ink line under the feet, the lit floor below it
  if (!bust) for (let x = 0; x < w; x++) out[sill * w + x] = 1;
  // the figure in ink, with a paper rim where it meets the dark
  for (let y = 0; y < sill; y++) for (let x = 0; x < w; x++) {
    if (fig[y * w + x]) continue;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const X = x + dx, Y = y + dy;
      if (X >= 0 && Y >= 0 && X < w && Y < h && fig[Y * w + X]) { out[y * w + x] = 0; break; }
    }
  }
  for (let i = 0; i < out.length; i++) if (fig[i]) out[i] = 1;
  // the eyes, where the knock draws them. Pinch's are paper on ink, Lintel's ink on paper. In a close crop (under 2.5 mask
  // px a pixel: the overview card's bust) one pixel is lost among the rider's gaps, so each eye is 2x2 in a clean ring of
  // the figure's colour (sprint 21 fix pass); the envelope's small crop and the whole figure keep one pixel
  const eye = look === 'pinch' || look === 'holden' ? 0 : 1, big = k < 2.5;
  const put = (x, y, v) => { if (x >= 0 && y >= 0 && x < w && y < sill) out[y * w + x] = v; };
  // Pinch's face in a close crop: a solid ink block round the pair, so the inner legs' slits beside and under it do not
  // read as more eyes (the outer legs and the knees stay)
  if (big && look === 'pinch') {
    const eyesAt = M.eyes.map(([ex, ey]) => [Math.floor(w / 2 + (ex - M.cx) / k) - (ex < M.cx ? 1 : 0), Math.floor(HEAD + (ey - TOP) / k)]);
    const xs = eyesAt.map(a => a[0]), ys = eyesAt.map(a => a[1]);
    for (let y = Math.min(...ys) - 3; y <= Math.max(...ys) + 8; y++) for (let x = Math.min(...xs) - 10; x <= Math.max(...xs) + 11; x++) put(x, y, 1);
  }
  for (const [ex, ey] of M.eyes) {
    const x = Math.floor(w / 2 + (ex - M.cx) / k), y = Math.floor(HEAD + (ey - TOP) / k);
    if (!big) { put(x, y, eye); continue; }
    const x0 = ex < M.cx ? x - 1 : x; // the pair spreads outward from the centre, so the two rings never touch
    for (let j = -1; j <= 2; j++) for (let i = -1; i <= 2; i++) put(x0 + i, y + j, 1 - eye);
    for (let j = 0; j <= 1; j++) for (let i = 0; i <= 1; i++) put(x0 + i, y + j, eye);
  }
  return out;
}

// Paint the picture on a canvas at its own size (CSS scales it up, image-rendering: pixelated). Returns the canvas.
export function drawLitIcon(canvas, look, opts) {
  const w = canvas.width, h = canvas.height, bits = litIconBits(look, w, h, opts), g = canvas.getContext?.('2d');
  if (!bits || !g) return canvas;
  const img = g.createImageData(w, h);
  for (let i = 0; i < bits.length; i++) { const v = bits[i] ? 0 : 255; img.data[i * 4] = img.data[i * 4 + 1] = img.data[i * 4 + 2] = v; img.data[i * 4 + 3] = 255; }
  g.putImageData(img, 0, 0);
  return canvas;
}

// A canvas with the picture, hidden from screen readers (the words beside it name the neighbour).
export function litIcon(doc, look, { w = 60, h = 80, bust = false } = {}) {
  const c = doc.createElement('canvas');
  c.width = w; c.height = h; c.className = 'lit-icon';
  c.setAttribute('aria-hidden', 'true');
  return drawLitIcon(c, look, { bust });
}
